import { Anchor, Button, PasswordInput, PinInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sessionApi, useBrand, slugDaPorta } from '@eva/domain';
import { problemaNaSenhaDePaciente } from '@eva/schemas';
import { AuthFrame } from './AuthFrame';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const { brand, detection } = useBrand();
  const navigate = useNavigate();
  const [enviando, setEnviando] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  const form = useForm({
    initialValues: { code: '', password: '', confirm: '' },
    validate: {
      code: (v) => (v.length === 6 ? null : 'Informe os 6 dígitos'),
      // A regra de paciente (A085). Conta de equipe tem regra mais forte, que
      // só o servidor conhece: a mensagem dele aparece no envio.
      password: (v) => problemaNaSenhaDePaciente(v, { email }),
      confirm: (v, values) => (v === values.password ? null : 'As senhas não conferem'),
    },
  });

  const redefinir = form.onSubmit(async ({ code, password }) => {
    setEnviando(true);
    try {
      await sessionApi.resetPassword(email, code, password);
      notifications.show({ color: 'green', title: 'Senha redefinida', message: 'Entre com a nova senha.' });
      navigate('/entrar', { replace: true });
    } catch (e: any) {
      notifications.show({ color: 'red', title: 'Não foi possível redefinir', message: e?.message ?? 'Confira o código e tente de novo.' });
    } finally {
      setEnviando(false);
    }
  });

  const reenviar = async () => {
    setReenviando(true);
    try {
      await sessionApi.forgotPassword(email, slugDaPorta(brand, detection));
      notifications.show({ color: 'green', message: 'Código reenviado.' });
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.message ?? 'Tente novamente.' });
    } finally {
      setReenviando(false);
    }
  };

  if (!email) {
    return (
      <AuthFrame title="Redefinir senha">
        <Text ta="center">
          Comece por <Anchor component={Link} to="/esqueci-senha">informar seu email</Anchor>.
        </Text>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Digite o código" subtitle={`Enviado para ${email}`}>
      <form onSubmit={redefinir}>
        <Stack gap="md">
          <Stack align="center" gap={4}>
            <PinInput length={6} type="number" size="md" oneTimeCode autoFocus {...form.getInputProps('code')} />
            {form.errors.code && <Text c="red" fz="xs">{form.errors.code}</Text>}
          </Stack>
          <PasswordInput label="Nova senha" placeholder="Mínimo 8 caracteres" autoComplete="new-password" size="md" {...form.getInputProps('password')} />
          <PasswordInput label="Confirme a nova senha" placeholder="Repita a senha" autoComplete="new-password" size="md" {...form.getInputProps('confirm')} />
          <Button type="submit" size="md" loading={enviando} fullWidth>
            Redefinir senha
          </Button>
          <Text fz="sm" ta="center">
            <Anchor component="button" type="button" fz="sm" onClick={reenviar} disabled={reenviando}>
              Reenviar código
            </Anchor>
          </Text>
        </Stack>
      </form>
    </AuthFrame>
  );
}
